import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Alert,
  bgColor,
  Button,
  Col,
  humps,
  LoadingOutlined,
  message,
  Row,
  Typography,
  useTranslation,
} from '@lib/index';
import { useGetUploadQuery } from '@services/upload/upload.service';
import { Upload } from '@services/upload/types';
import { ManualMarkImportContext } from './context';
import ManualMarkImportResult from './ManualMarkImportResult';
import { ManualMarkImportTracking } from './types';

const STORAGE_KEY = 'manualMarkImportTracking';
const TERMINAL_STATUSES = ['completed', 'failed'];

const storedTracking = (): ManualMarkImportTracking | null => {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value ? (JSON.parse(value) as ManualMarkImportTracking) : null;
  } catch (_error) {
    return null;
  }
};

const ManualMarkImportProvider = ({ children }: { children: ReactNode }) => {
  const { t } = useTranslation();
  const [tracking, setTracking] = useState<ManualMarkImportTracking | null>(
    storedTracking,
  );
  const [resultVisible, setResultVisible] = useState(false);
  const [pollingEnabled, setPollingEnabled] = useState(Boolean(tracking));
  const notifiedUploadRef = useRef<number | null>(null);
  const { data } = useGetUploadQuery(tracking?.uploadId || 0, {
    skip: !tracking,
    pollingInterval: tracking && pollingEnabled ? 5000 : 0,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });
  const upload = data ? (humps(data) as Upload) : null;
  const isTerminal = upload?.importStatus
    ? TERMINAL_STATUSES.includes(upload.importStatus)
    : false;

  useEffect(() => {
    if (!tracking || !isTerminal || notifiedUploadRef.current === tracking.uploadId) return;
    notifiedUploadRef.current = tracking.uploadId;
    setPollingEnabled(false);
    setResultVisible(true);
    if (upload?.importStatus === 'failed') {
      message.error(t('workerAccess.manualMark.import.failed'));
    } else if (
      (upload?.summary?.failed || 0) > 0
      || (upload?.summary?.errors?.length || 0) > 0
    ) {
      message.warning(t('workerAccess.manualMark.import.validationFailed'));
    } else {
      message.success(
        t('workerAccess.manualMark.import.completed', {
          count: upload?.summary?.created || 0,
        }),
      );
    }
  }, [isTerminal, t, tracking, upload]);

  const trackImport = useCallback((value: ManualMarkImportTracking) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    notifiedUploadRef.current = null;
    setResultVisible(false);
    setPollingEnabled(true);
    setTracking(value);
  }, []);

  const closeResult = () => {
    localStorage.removeItem(STORAGE_KEY);
    setResultVisible(false);
    setTracking(null);
  };

  const contextValue = useMemo(() => ({ trackImport }), [trackImport]);
  const summary = upload?.summary;
  const processed = summary?.processed || 0;
  const total = summary?.total || 0;

  return (
    <ManualMarkImportContext.Provider value={contextValue}>
      {children}
      {tracking && !isTerminal && (
        <Row
          className="fixed top-20 left-1/2 z-50 w-11/12 md:w-2/3 lg:w-1/2 transform -translate-x-1/2 shadow-lg"
        >
          <Col span={24}>
            <Alert
              type="info"
              showIcon
              className={`${bgColor.lightBlue} border-blue-200`}
              icon={<LoadingOutlined spin />}
              message={t('workerAccess.manualMark.import.processing')}
              description={(
                <Row>
                  <Col span={24}>
                    <Typography.Text className="block">
                      {tracking.fileName}
                    </Typography.Text>
                  </Col>
                  <Col span={24}>
                    <Typography.Text type="secondary">
                      {total > 0
                        ? t('workerAccess.manualMark.import.progress', {
                          processed,
                          total,
                        })
                        : t('workerAccess.manualMark.import.preparing')}
                    </Typography.Text>
                  </Col>
                </Row>
            )}
            />
          </Col>
        </Row>
      )}
      {tracking && isTerminal && !resultVisible && (
        <Button
          type="primary"
          className="fixed bottom-6 right-6 z-50"
          onClick={() => setResultVisible(true)}
        >
          {t('workerAccess.manualMark.import.viewResult')}
        </Button>
      )}
      {tracking && upload && (
        <ManualMarkImportResult
          fileName={tracking.fileName}
          upload={upload}
          visible={resultVisible}
          onClose={closeResult}
        />
      )}
    </ManualMarkImportContext.Provider>
  );
};

export default ManualMarkImportProvider;
